import React, { useEffect, useMemo, useCallback } from '../../../_snowpack/pkg/react.js';
import PropTypes from '../../../_snowpack/pkg/prop-types.js';
import styled from '../../../_snowpack/pkg/styled-components.js';
import is from '../../../_snowpack/pkg/styled-is.js';
import { useDrop } from '../../../_snowpack/pkg/react-dnd.js';
import { v4 as uuid } from '../../../_snowpack/pkg/uuid.js';
import lighten from '../../../_snowpack/pkg/polished/lib/color/lighten.js';
import { colors } from '../../css/index.js';
import Drag from './drag.js';
import { MediasDndEvents } from './index.js';
import { dropHelper, reorderItemsHelper } from './drop.helpers.js';
const Container = styled.div.withConfig({
  displayName: "drop__Container",
  componentId: "qc6dcb-0"
})(["margin:0;width:100%;height:100%;padding:1px;border-radius:5px;", ""], is('canDrop')`
    outline: 2px dashed ${lighten(0.25, colors.glacier)};
    outline-offset: 5px;
  `);
/**
 * Component that will render a droppable for the types allowed
 * and will also create draggable components for each item in items
 */

function Drop({
  accept,
  alignChildren,
  as,
  children,
  className,
  items,
  onChange,
  onDropCallback,
  renderItem
}) {
  // Uniquely identify this drop container
  const DROP_CONTAINER_ID = useMemo(() => uuid(), []);
  const insertItem = useCallback(({
    index,
    item
  }) => {
    const newItems = [...items];
    newItems.splice(index, 0, item);
    onChange(newItems);
  }, [items, onChange]); // Insert copy of an item at the end of a drag operation

  useEffect(() => {
    function onInsertCopyItem({
      item,
      index,
      parentId
    }) {
      if (parentId === DROP_CONTAINER_ID) {
        const id = uuid();
        insertItem({
          index,
          item: { ...item,
            id,
            media: { ...item.media,
              id
            }
          }
        });
      }
    }

    MediasDndEvents.on('insertCopyItem', onInsertCopyItem);
    return () => MediasDndEvents.off('insertCopyItem', onInsertCopyItem);
  }, [insertItem, items, DROP_CONTAINER_ID]);

  function removeItem(id) {
    onChange(items.filter(i => i.id !== id));
  }

  function reorderItems({
    fromIndex,
    toIndex
  }) {
    reorderItemsHelper({
      fromIndex,
      items,
      onChange,
      removeItem,
      toIndex
    });
  }

  const [{
    canDrop
  }, dropRef] = useDrop({
    accept,
    collect: monitor => ({
      canDrop: monitor.canDrop()
    }),
    hover: (item, monitor) => {
      if (monitor.isOver({
        shallow: true
      })) {
        dropHelper({
          item,
          items,
          removeItem,
          monitor,
          onChange,
          onDropCallback,
          DROP_CONTAINER_ID
        });
      }
    }
  });
  return /*#__PURE__*/React.createElement(Container, {
    as: as,
    className: className,
    ref: dropRef,
    canDrop: canDrop
  }, alignChildren === 'start' && children, items.length ? items.map((item, index) => /*#__PURE__*/React.createElement(Drag, {
    key: item.id,
    index: index,
    item: item,
    type: item.type,
    parentId: DROP_CONTAINER_ID,
    removeItem: removeItem,
    reorderItems: reorderItems,
    insertItem: insertItem
  }, renderItem(item, index))) : null, alignChildren === 'end' && children);
}

Drop.defaultProps = {
  alignChildren: 'start',
  items: []
};
Drop.propTypes = {
  /**
   * A list of items that the droppable container will accept
   */
  accept: PropTypes.arrayOf(PropTypes.string).isRequired,

  /**
   * Prop to place other content within the component
   */
  alignChildren: PropTypes.oneOf(['start', 'end']),

  /**
   * List of items to render. They will be wrapped with a drag container.
   * They must have two required params
   * @param id: Unique item identifier
   * @param type: The item type that will allow drop into a droppable container
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string.isRequired
  })),

  /**
   * Callback function that receive the updated list of items
   */
  onChange: PropTypes.func.isRequired,

  /**
   * Callback to be called when a new item its dropped into a drop container
   */
  onDropCallback: PropTypes.func,

  /**
   * React element that will render each item on the list of items
   */
  renderItem: PropTypes.elementType
};
export default Drop;